import { AVAILABLE_LANGUAGES, TEXT_TO_SPEECH_OPTIONS } from '@mission.io/mission-toolkit/constants'

type VoicePreferences = {
	preferred: string[],
	lastResort: string[], // these voices have problems and should not be used by the synthesizer unless no other option is available
}

type VoicePreference = {
	prefer: string, // the fully qualified voice language
	fallbackPrefix: string, // any voice which has this language
	voices: {
		MALE: VoicePreferences, // list of the male names for a specific language
		FEMALE: VoicePreferences, // list of the female names for a specific language
	},
}

export const LANGUAGE_TO_VOICE_LANGUAGE: {
	[language: $Keys<typeof AVAILABLE_LANGUAGES>]: VoicePreference,
} = {
	[AVAILABLE_LANGUAGES.ENGLISH]: {
		prefer: 'en-US',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [
					'Alex', // Mac
					'Aaron',
					'David', // Windows
				],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: [
					'Victoria', // Mac
					'Nicky',
					'Google US English',
					'Zira', // Windows
				],
				lastResort: [],
			},
		},
		fallbackPrefix: 'en',
	},
	[AVAILABLE_LANGUAGES.SPANISH]: {
		prefer: 'es-MX',
		fallbackPrefix: 'es',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [
					'Eddy (Spanish (Mexico))', // Mac
				],
				lastResort: [
					'Google español', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
					'Google español de Estados Unidos', // same problem as 'Google español'
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Flo (Spanish (Mexico))'],
				lastResort: [
					'Google español', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
					'Google español de Estados Unidos', // same problem as 'Google español'
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.PORTUGUESE]: {
		prefer: 'pt-BR',
		fallbackPrefix: 'pt',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Grandpa (Portuguese (Brazil))', 'Eddy (Portuguese (Brazil))', 'Joana'],
				lastResort: [
					'Google português do Brasil', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Luciana', 'Flo (Portuguese (Brazil))', 'Grandma (Portuguese (Brazil))'],
				lastResort: [
					'Google português do Brasil', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.FRENCH]: {
		prefer: 'fr-FR',
		fallbackPrefix: 'fr',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [],
				lastResort: [
					'Google français', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: [],
				lastResort: [
					'Google français', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.ARMENIAN]: {
		prefer: 'hy',
		fallbackPrefix: 'hy',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: [],
				lastResort: [],
			},
		},
	},
	[AVAILABLE_LANGUAGES.RUSSIAN]: {
		prefer: 'ru-RU',
		fallbackPrefix: 'ru',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [],
				lastResort: [
					'Google русский', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: [],
				lastResort: [
					'Google русский', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.TAGALOG]: {
		prefer: 'tl',
		fallbackPrefix: 'tl',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: [],
				lastResort: [],
			},
		},
	},
	[AVAILABLE_LANGUAGES.GERMAN]: {
		prefer: 'de-DE',
		fallbackPrefix: 'de',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Anna', 'Grandpa (German (Germany))', 'Martin'],
				lastResort: [
					'Google Deutsch', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Grandma (German (Germany))', 'Helena'],
				lastResort: [
					'Google Deutsch', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.UKRAINIAN]: {
		prefer: 'uk-UA',
		fallbackPrefix: 'uk',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Lesya'],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Lesya'],
				lastResort: [],
			},
		},
	},
	[AVAILABLE_LANGUAGES.ARABIC]: {
		prefer: 'ar-001',
		fallbackPrefix: 'ar',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Majed'],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Majed'],
				lastResort: [],
			},
		},
	},
	[AVAILABLE_LANGUAGES.CANTONESE]: {
		prefer: 'yue',
		fallbackPrefix: 'yue',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: [],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: [],
				lastResort: [],
			},
		},
	},
	[AVAILABLE_LANGUAGES.JAPANESE]: {
		prefer: 'ja-JP',
		fallbackPrefix: 'ja',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Hattori'],
				lastResort: [
					'Google 日本語', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['O-Ren', 'Kyoko'],
				lastResort: [
					'Google 日本語', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.KOREAN]: {
		prefer: 'ko-KR',
		fallbackPrefix: 'ko',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Yuna'],
				lastResort: [
					'Google 한국의', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Yuna'],
				lastResort: [
					'Google 한국의', // this voice is better but completely breaks the speech synthesizer (it assumes the vocal track never ends and does not give boundary updates), there are some work around in the code so that it is still "usable", but it is not as pleasant of an experience. Do not use if there is any other available voice.
				],
			},
		},
	},
	[AVAILABLE_LANGUAGES.VIETNAMESE]: {
		prefer: 'vi-VN',
		fallbackPrefix: 'vi',
		voices: {
			[TEXT_TO_SPEECH_OPTIONS.MALE]: {
				preferred: ['Linh'],
				lastResort: [],
			},
			[TEXT_TO_SPEECH_OPTIONS.FEMALE]: {
				preferred: ['Linh'],
				lastResort: [],
			},
		},
	},
}
